import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { UnionDomain } from 'src/app/services/union.domain';
import { SubdomainService } from 'src/app/services/subdomain.service';
import { WardService } from 'src/app/services/ward.service';
import { SmsService } from 'src/app/services/sms.service';
import { environment } from 'src/environments/environment';
import * as XLSX from 'xlsx';

interface TaxCollectionRecord {
  id: number;
  assessment_id: number;
  name: string;
  gurdian_name?: string;
  mobile_number?: string;
  holding_number: string;
  ward_number: number;
  gram_or_rasta: string;
  financial_year: string;
  tax_amount: string;
  collection_date?: string;
  assessment_qr_code: string;
  debit_credit?: string;
  khanar_dhoron: number;
  total_credit?: number;
  total_debit?: number;
  outstanding_amount?: number;
}

interface BulkSmsData {
  message: string;
  recipients: string[];
}

@Component({
  selector: 'app-tax-collection-list',
  templateUrl: './tax-collection-list.component.html',
  styleUrls: ['./tax-collection-list.component.scss']
})
export class TaxCollectionListComponent implements OnInit {
  // Tab management
  activeTab: string = 'all';
  
  // Data management
  data: TaxCollectionRecord[] = [];
  paginatedData: TaxCollectionRecord[] = [];
  
  // Pagination
  currentPage: number = 1;
  itemsPerPage: number | 'All' = 10;
  totalPages: number = 0;
  totalItems: number = 0;
  pageSizes: (number | string)[] = [10, 20, 50, 100, 500, 1000, 'All'];
  
  // Sorting and filtering
  currentSortColumn: string = 'createdAt';
  currentSortDirection: 'asc' | 'desc' = 'desc';
  filters: { [key: string]: string } = {};
  fullTextSearchTerm: string = '';
  
  // Other data
  wards: any[] = [];
  $subscriptions: Subscription[] = [];
  fileName: string = 'TaxCollectionList';
  isLoading: boolean = false; // Add loading state
  
  // Total calculation
  currentTotal: number = 0;
  displayedTotal: number = 0; // Total for currently displayed (paginated) data
  
  // Bulk SMS
  showBulkSmsModal: boolean = false;
  showRecipientsList: boolean = false;
  bulkSmsData: BulkSmsData = {
    message: '',
    recipients: []
  };
  smsCharCount: number = 0;
  smsPartsCount: number = 0;
  remainingChars: number = 160;
  
  // Financial year helper
  currentFinancialYear: number = new Date().getFullYear();
  
  private apiUrl = environment.apiBaseUrl;
  
  // ViewChild references for filters
  @ViewChild('holdingNumberFilter') holdingNumberFilter!: ElementRef;
  @ViewChild('nameFilter') nameFilter!: ElementRef;
  @ViewChild('guardianNameFilter') guardianNameFilter!: ElementRef;
  @ViewChild('wardNumberFilter') wardNumberFilter!: ElementRef;
  @ViewChild('mobileNumberFilter') mobileNumberFilter!: ElementRef;
  @ViewChild('gramOrRastaFilter') gramOrRastaFilter!: ElementRef;
  @ViewChild('startDateFilter') startDateFilter!: ElementRef;
  @ViewChild('endDateFilter') endDateFilter!: ElementRef;

  constructor(
    private http: HttpClient,
    private router: Router,
    public unionDomain: UnionDomain,
    private toastr: ToastrService,
    private subdomainService: SubdomainService,
    private wardService: WardService,
    private smsService: SmsService
  ) {}

  ngOnInit(): void {
    this.fileName = `${this.activeTab}_tax_collection_${new Date().toISOString().split('T')[0]}`;
    this.getWards();
    this.loadData();
  }

  ngOnDestroy(): void {
    this.$subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  // Helper method to format financial year for display
  formatFinancialYear(year: number | string): string {
    const yearNum = typeof year === 'string' ? parseInt(year) : year;
    return `${yearNum}-${yearNum + 1}`;
  }

  // Ward management
  private getWards = async (): Promise<void> => {
    this.$subscriptions.push(
      this.wardService.getWardList().subscribe({
        next: (res: any) => {
          this.wards = res || [];
        },
        error: (error) => {
          console.error('Error fetching wards:', error);
          this.wards = [];
        }
      })
    );
  };

  // Tab switching
  switchTab(tab: string): void {
    this.activeTab = tab;
    this.currentPage = 1;
    this.clearFilters();
    this.fileName = `${tab}_tax_collection_${new Date().toISOString().split('T')[0]}`;
    this.loadData();
  }

  // Data loading
  loadData(): void {
    this.isLoading = true;
    
    const params: any = {
      type: this.activeTab,
      subdomain: this.subdomainService.getSubdomain(),
      sortBy: this.currentSortColumn,
      sortOrder: this.currentSortDirection.toUpperCase(),
      ...this.filters,
    };

    // Add pagination parameters only if not requesting "All"
    if (this.itemsPerPage !== 'All') {
      params.pageNumber = this.currentPage.toString();
      params.pageSize = this.itemsPerPage.toString();
    }

    // Convert params to query string
    const queryParams = new URLSearchParams();
    
    // Add all regular params
    Object.keys(params).forEach(key => {
      queryParams.append(key, params[key]);
    });
    
    // Add search term parameter separately if it exists
    if (this.fullTextSearchTerm && this.fullTextSearchTerm.trim() !== '') {
      queryParams.append('searchTerm', this.fullTextSearchTerm.trim());
    }
    
    // Complete URL with query string
    const fullUrl = `${this.apiUrl}/api/taxCollection/getFilteredTaxCollections?${queryParams.toString()}`;

    // Make the HTTP request
    this.http.get<any>(fullUrl).subscribe({
      next: (response) => {
        this.data = response.data;
        this.totalPages = response.totalPages;
        this.totalItems = response.totalCount;
        this.paginatedData = this.data;
        this.calculateTotals(); // Calculate totals after data is loaded
        this.isLoading = false;
      },
      error: (error) => {
        this.toastr.error('Error loading tax collection data');
        console.error('Error:', error);
        this.isLoading = false;
      }
    });
  }

  // Search functionality
  performFullTextSearch(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.fullTextSearchTerm = target.value;
    this.currentPage = 1;
    this.loadData();
  }

  // Filtering
  filterByWard(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    const wardNumber = selectElement.value;
    
    if (wardNumber) {
      this.filters['filter[ward_number]'] = wardNumber;
    } else {
      delete this.filters['filter[ward_number]'];
    }
    this.loadData();
  }

  filterData(event: any, column: string): void {
    const filterValue = event.target.value.toLowerCase();
    if (filterValue) {
      this.filters[`filter[${column}]`] = filterValue;
    } else {
      delete this.filters[`filter[${column}]`];
    }
    this.currentPage = 1;
    this.loadData();
  }

  // Date range filtering
  filterByDateRange(): void {
    const startDate = this.startDateFilter?.nativeElement?.value;
    const endDate = this.endDateFilter?.nativeElement?.value;

    if (startDate) {
      this.filters['filter[start_date]'] = startDate;
    } else {
      delete this.filters['filter[start_date]'];
    }

    if (endDate) {
      this.filters['filter[end_date]'] = endDate;
    } else {
      delete this.filters['filter[end_date]'];
    }

    this.currentPage = 1;
    this.loadData();
  }

  // Clear all filters
  clearFilters(): void {
    this.filters = {};
    this.fullTextSearchTerm = '';
    
    // Reset filter inputs
    if (this.holdingNumberFilter?.nativeElement) this.holdingNumberFilter.nativeElement.value = '';
    if (this.nameFilter?.nativeElement) this.nameFilter.nativeElement.value = '';
    if (this.guardianNameFilter?.nativeElement) this.guardianNameFilter.nativeElement.value = '';
    if (this.wardNumberFilter?.nativeElement) this.wardNumberFilter.nativeElement.value = '';
    if (this.mobileNumberFilter?.nativeElement) this.mobileNumberFilter.nativeElement.value = '';
    if (this.gramOrRastaFilter?.nativeElement) this.gramOrRastaFilter.nativeElement.value = '';
    
    // Reset date filters
    if (this.startDateFilter?.nativeElement) this.startDateFilter.nativeElement.value = '';
    if (this.endDateFilter?.nativeElement) this.endDateFilter.nativeElement.value = '';
    
    // Reset search input
    const searchInput = document.getElementById('fullTextSearch') as HTMLInputElement;
    if (searchInput) {
      searchInput.value = '';
    }
    
    // Reset ward dropdown
    const wardDropdown = document.getElementById('wardDropdown') as HTMLSelectElement;
    if (wardDropdown) {
      wardDropdown.value = '';
    }
    
    this.loadData();
  }

  // Sorting
  sortData(column: string): void {
    if (this.currentSortColumn === column) {
      this.currentSortDirection = this.currentSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.currentSortColumn = column;
      this.currentSortDirection = 'asc';
    }
    this.loadData();
  }

  // Pagination
  changePage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadData();
    }
  }

  changePageSize(event: any): void {
    this.itemsPerPage = event.target.value === 'All' ? 'All' : +event.target.value;
    this.currentPage = 1;
    this.loadData();
  }

  getPaginationPages(): number[] {
    const pages: number[] = [];
    const maxPagesToShow = 5;
    const halfPagesToShow = Math.floor(maxPagesToShow / 2);

    let startPage = Math.max(1, this.currentPage - halfPagesToShow);
    let endPage = Math.min(this.totalPages, this.currentPage + halfPagesToShow);

    if (this.currentPage - halfPagesToShow < 1) {
      endPage = Math.min(this.totalPages, endPage + (halfPagesToShow - (this.currentPage - 1)));
    }

    if (this.currentPage + halfPagesToShow > this.totalPages) {
      startPage = Math.max(1, startPage - (this.currentPage + halfPagesToShow - this.totalPages));
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  }

  // Export functionality
  exportTableToExcel(): void {
    const element = document.getElementById('taxCollectionTable');
    const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(element);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, `${this.fileName}.xlsx`);
  }

  // Receipt functionality
  viewReceipt(record: TaxCollectionRecord): void {
    if (this.activeTab === 'all' && record.id) {
      this.router.navigate(['admin/tax-collection-receipt'], {
        queryParams: { id: record.id }
      });
    } else {
      this.toastr.info('Receipt not available for this record');
    }
  }

  // Navigation methods
  navigateToTaxCollection(qrCode: string): void {
    this.router.navigate(['admin/tax-collection'], {
      queryParams: { qr_code: qrCode }
    });
  }

  // Bulk SMS functionality
  openBulkSmsModal(): void {
    if (this.data.length === 0) {
      this.toastr.warning('No data available for SMS');
      return;
    }

    // Collect mobile numbers from current data
    this.bulkSmsData.recipients = this.data
      .filter(record => record.mobile_number && record.mobile_number.trim() !== '')
      .map(record => record.mobile_number!)
      .filter((number, index, self) => self.indexOf(number) === index); // Remove duplicates

    if (this.bulkSmsData.recipients.length === 0) {
      this.toastr.warning('No mobile numbers available for SMS');
      return;
    }

    // Set default message based on tab
    let defaultMessage = '';
    switch (this.activeTab) {
      case 'unpaid':
        defaultMessage = 'আপনার হোল্ডিং ট্যাক্স বাকি রয়েছে। অনুগ্রহ করে শীঘ্রই পরিশোধ করুন।';
        break;
      case 'paid':
        defaultMessage = 'আপনার হোল্ডিং ট্যাক্স সম্পূর্ণ পরিশোধিত হয়েছে। ধন্যবাদ।';
        break;
      default:
        defaultMessage = 'ট্যাক্স সংক্রান্ত গুরুত্বপূর্ণ তথ্য।';
    }

    this.bulkSmsData.message = defaultMessage;
    this.updateSmsCharacterCount();
    this.showRecipientsList = false; // Reset recipients list visibility
    this.showBulkSmsModal = true;
  }

  closeBulkSmsModal(): void {
    this.showBulkSmsModal = false;
    this.showRecipientsList = false;
    this.bulkSmsData = {
      message: '',
      recipients: []
    };
  }

  toggleRecipientsList(): void {
    this.showRecipientsList = !this.showRecipientsList;
  }

  updateSmsCharacterCount(): void {
    this.smsCharCount = this.bulkSmsData.message.length;
    this.smsPartsCount = Math.ceil(this.smsCharCount / 160);
    this.remainingChars = Math.max(160 - (this.smsCharCount % 160), 0);
  }

  sendBulkSms(): void {
    if (!this.bulkSmsData.message.trim()) {
      this.toastr.warning('Please enter a message');
      return;
    }

    if (this.bulkSmsData.recipients.length === 0) {
      this.toastr.warning('No recipients available');
      return;
    }

    const recipientString = this.bulkSmsData.recipients.join(',');
    this.smsService.sendQuickSmsToMultipleNumbers(recipientString, this.bulkSmsData.message);
    
    this.toastr.success(`SMS sent to ${this.bulkSmsData.recipients.length} recipients`);
    this.closeBulkSmsModal();
  }

  // Utility methods
  getTabTitle(): string {
    switch (this.activeTab) {
      case 'all':
        return 'সকল ট্যাক্স কালেকশন';
      case 'unpaid':
        return 'অপরিশোধিত ট্যাক্স';
      case 'paid':
        return 'পরিশোধিত ট্যাক্স';
      default:
        return 'ট্যাক্স কালেকশন তালিকা';
    }
  }

  getStatusBadgeClass(record: TaxCollectionRecord): string {
    if (this.activeTab === 'unpaid') {
      return 'badge badge-danger';
    } else if (this.activeTab === 'paid') {
      return 'badge badge-success';
    }
    return record.debit_credit === 'D' ? 'badge badge-success' : 'badge badge-warning';
  }

  getStatusText(record: TaxCollectionRecord): string {
    if (this.activeTab === 'unpaid') {
      return 'বকেয়া';
    } else if (this.activeTab === 'paid') {
      return 'পরিশোধিত';
    }
    return record.debit_credit === 'D' ? 'আদায়' : 'বরাদ্দ';
  }

  // TrackBy function for performance optimization
  trackByRecordId(index: number, record: TaxCollectionRecord): number {
    return record.id;
  }

  // Total calculation methods
  calculateTotals(): void {
    // Calculate total for all filtered data
    this.currentTotal = this.data.reduce((total, record) => {
      const amount = parseFloat(record.tax_amount) || 0;
      if (this.activeTab === 'all') {
        // For 'all' tab, consider debit/credit
        return total + (record.debit_credit === 'D' ? amount : -amount);
      } else {
        // For unpaid/paid tabs, just sum the amounts
        return total + amount;
      }
    }, 0);

    // Calculate total for currently displayed (paginated) data
    this.displayedTotal = this.paginatedData.reduce((total, record) => {
      const amount = parseFloat(record.tax_amount) || 0;
      if (this.activeTab === 'all') {
        // For 'all' tab, consider debit/credit
        return total + (record.debit_credit === 'D' ? amount : -amount);
      } else {
        // For unpaid/paid tabs, just sum the amounts
        return total + amount;
      }
    }, 0);
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('bn-BD', {
      style: 'currency',
      currency: 'BDT',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  }

  formatNumber(amount: number): string {
    return new Intl.NumberFormat('bn-BD', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  }
}
